/**
 * Parent account deletion — 15-day grace period before permanent removal
 * of parent and all linked child accounts.
 */

export const ACCOUNT_DELETION_STORAGE_KEY = "myTARA_accountDeletion";
export const PERMANENTLY_DELETED_STORAGE_KEY = "myTARA_permanentlyDeletedAccounts";
export const GRACE_PERIOD_DAYS = 15;
export const GRACE_PERIOD_MS = GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000;

export type AccountDeletionStatus = "pending" | "cancelled" | "permanent";

export interface LinkedAccountRef {
  userId?: string;
  patientId: string;
  name: string;
}

export interface AccountDeletionRecord {
  parentUserId: string;
  parentEmail?: string;
  parentName?: string;
  requestedAt: string;
  permanentDeletionAt: string;
  linkedAccounts: LinkedAccountRef[];
  status: AccountDeletionStatus;
}

export interface AccountDeletionCountdown {
  days: number;
  hours: number;
  minutes: number;
  totalMs: number;
  isExpired: boolean;
}

function readDeletionMap(): Record<string, AccountDeletionRecord> {
  try {
    const raw = localStorage.getItem(ACCOUNT_DELETION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeDeletionMap(map: Record<string, AccountDeletionRecord>) {
  localStorage.setItem(ACCOUNT_DELETION_STORAGE_KEY, JSON.stringify(map));
}

function readPermanentlyDeleted(): string[] {
  try {
    const raw = localStorage.getItem(PERMANENTLY_DELETED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function writePermanentlyDeleted(ids: string[]) {
  localStorage.setItem(PERMANENTLY_DELETED_STORAGE_KEY, JSON.stringify(ids));
}

export function isPermanentlyDeleted(userId: string | undefined | null): boolean {
  if (!userId) return false;
  return readPermanentlyDeleted().includes(String(userId));
}

export function getAccountDeletionRecord(
  parentUserId: string | undefined | null
): AccountDeletionRecord | null {
  if (!parentUserId) return null;
  const record = readDeletionMap()[String(parentUserId)];
  if (record?.status !== "pending") return null;
  return record;
}

export function getPendingDeletionRecord(
  parentUserId: string | undefined | null
): AccountDeletionRecord | null {
  const record = getAccountDeletionRecord(parentUserId);
  if (!record) return null;
  if (new Date(record.permanentDeletionAt).getTime() <= Date.now()) {
    return null;
  }
  return record;
}

export function getDeletionCountdown(
  permanentDeletionAt: string
): AccountDeletionCountdown {
  const totalMs = Math.max(0, new Date(permanentDeletionAt).getTime() - Date.now());
  const isExpired = totalMs <= 0;
  const days = Math.floor(totalMs / (24 * 60 * 60 * 1000));
  const hours = Math.floor((totalMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((totalMs % (60 * 60 * 1000)) / (60 * 1000));
  return { days, hours, minutes, totalMs, isExpired };
}

export function formatDeletionCountdown(countdown: AccountDeletionCountdown): string {
  if (countdown.isExpired) return "0 days";
  const parts: string[] = [];
  if (countdown.days > 0) parts.push(`${countdown.days} day${countdown.days === 1 ? "" : "s"}`);
  if (countdown.hours > 0) parts.push(`${countdown.hours} hr${countdown.hours === 1 ? "" : "s"}`);
  if (countdown.days === 0 && countdown.minutes > 0) {
    parts.push(`${countdown.minutes} min${countdown.minutes === 1 ? "" : "s"}`);
  }
  return parts.length ? parts.join(" ") : "less than 1 min";
}

export function scheduleAccountDeletion(input: {
  parentUserId: string;
  parentEmail?: string;
  parentName?: string;
  linkedAccounts: LinkedAccountRef[];
}): AccountDeletionRecord {
  const now = new Date();
  const permanentDeletionAt = new Date(now.getTime() + GRACE_PERIOD_MS);
  const record: AccountDeletionRecord = {
    parentUserId: String(input.parentUserId),
    parentEmail: input.parentEmail,
    parentName: input.parentName,
    requestedAt: now.toISOString(),
    permanentDeletionAt: permanentDeletionAt.toISOString(),
    linkedAccounts: input.linkedAccounts,
    status: "pending",
  };

  const map = readDeletionMap();
  map[record.parentUserId] = record;
  writeDeletionMap(map);
  clearDeletionRestoreDismiss(record.parentUserId);
  return record;
}

export function cancelAccountDeletion(parentUserId: string | undefined | null): boolean {
  if (!parentUserId) return false;
  const map = readDeletionMap();
  const key = String(parentUserId);
  const existing = map[key];
  if (existing?.status !== "pending") return false;

  map[key] = { ...existing, status: "cancelled" };
  writeDeletionMap(map);
  return true;
}

export function markAccountPermanentlyDeleted(
  record: AccountDeletionRecord
): void {
  const map = readDeletionMap();
  map[record.parentUserId] = { ...record, status: "permanent" };

  const deletedIds = new Set(readPermanentlyDeleted());
  deletedIds.add(record.parentUserId);
  record.linkedAccounts.forEach((a) => {
    if (a.userId) deletedIds.add(String(a.userId));
    deletedIds.add(String(a.patientId));
  });

  writeDeletionMap(map);
  writePermanentlyDeleted(Array.from(deletedIds));
}

/**
 * If grace period ended, permanently delete parent + linked accounts in local state.
 * Returns the record if enforcement happened.
 */
export function enforcePermanentDeletionIfDue(
  parentUserId: string | undefined | null
): AccountDeletionRecord | null {
  const record = getAccountDeletionRecord(parentUserId);
  if (!record) return null;

  const countdown = getDeletionCountdown(record.permanentDeletionAt);
  if (!countdown.isExpired) return null;

  markAccountPermanentlyDeleted(record);
  return record;
}

export function mapPatientListToLinkedAccounts(
  members: Array<Record<string, any>> | undefined | null
): LinkedAccountRef[] {
  if (!Array.isArray(members)) return [];
  return members
    .filter((m) => m?.patientId || m?.relatedUserId || m?.userId)
    .map((m) => {
      const linkedId = m.relatedUserId ?? m.userId;
      return {
        userId: linkedId ? String(linkedId) : undefined,
        patientId: String(m.patientId ?? linkedId),
        name:
          m.fullName ||
          [m.firstName, m.middleName, m.lastName].filter(Boolean).join(" ") ||
          "Linked account",
      };
    });
}

export function clearDeletionRestoreDismiss(parentUserId: string) {
  sessionStorage.removeItem(`accountDeletionRestoreDismissed:${parentUserId}`);
}
